import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Horse } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const COPY = {
  ar: {
    title: 'خيل العملاء الخارجيين',
    subtitle: 'إدارة خيل العملاء المستخدمة في سجلات العيادة والبيطار خارج الإسطبل.',
    addHorse: 'إضافة خيل',
    editHorse: 'تعديل خيل',
    refresh: 'تحديث',
    total: 'إجمالي الخيل',
    clients: 'العملاء',
    withRfid: 'لديها RFID',
    search: 'بحث باسم الخيل أو العميل أو RFID',
    horse: 'الخيل',
    origin: 'المصدر',
    breed: 'السلالة',
    gender: 'الجنس',
    rfid: 'RFID',
    color: 'اللون',
    client: 'العميل',
    actions: 'الإجراءات',
    registrationNumber: 'رقم التسجيل',
    registrationDate: 'تاريخ التسجيل',
    notes: 'ملاحظات',
    save: 'حفظ',
    cancel: 'إلغاء',
    mare: 'فرس',
    stallion: 'فحل',
    empty: 'لا توجد خيل مطابقة',
    loadError: 'تعذر تحميل البيانات.',
    deleteConfirm: 'هل تريد حذف هذا الخيل؟',
  },
  en: {
    title: 'Client Horses',
    subtitle: 'Manage outside client horses used in clinic and farrier records.',
    addHorse: 'Add Horse',
    editHorse: 'Edit Horse',
    refresh: 'Refresh',
    total: 'Total horses',
    clients: 'Clients',
    withRfid: 'With RFID',
    search: 'Search by horse, client, or RFID',
    horse: 'Horse',
    origin: 'Origin',
    breed: 'Breed',
    gender: 'Gender',
    rfid: 'RFID',
    color: 'Color',
    client: 'Client',
    actions: 'Actions',
    registrationNumber: 'Registration Number',
    registrationDate: 'Registration Date',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    mare: 'Mare',
    stallion: 'Stallion',
    empty: 'No matching horses',
    loadError: 'Could not load data.',
    deleteConfirm: 'Delete this horse?',
  },
};

const emptyHorse = {
  horse_name: '',
  breed: '',
  color_id: '',
  gender: '',
  rfid_chip_number: '',
  registration_number: '',
  registration_date: '',
  notes: '',
  client_id: '',
};

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toISOString().slice(0, 10);
}

function genderMeta(gender, text) {
  return gender === 'S'
    ? { label: text.stallion, icon: <MaleIcon fontSize="small" />, color: COLORS.blue, soft: COLORS.blueSoft }
    : { label: text.mare, icon: <FemaleIcon fontSize="small" />, color: COLORS.rose, soft: COLORS.roseSoft };
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 96 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: soft,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ color, fontWeight: 800 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
          {helper && <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

const HorsesClientsList = () => {
  const { i18n } = useTranslation();
  const text = COPY[i18n.language === 'ar' ? 'ar' : 'en'];
  const [horses, setHorses] = useState([]);
  const [clients, setClients] = useState([]);
  const [colors, setColors] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [horseData, setHorseData] = useState(emptyHorse);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const clientMap = useMemo(
    () => new Map(clients.map((client) => [Number(client.client_id), client.client_name])),
    [clients]
  );
  const colorMap = useMemo(
    () => new Map(colors.map((color) => [Number(color.color_id), color.color_english || color.color_arabic])),
    [colors]
  );

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [horsesRes, clientsRes, colorsRes, breedsRes] = await Promise.all([
        axios.get(`${apiUrl}/vetclinic-horses`),
        axios.get(`${apiUrl}/vetclinic-clients`),
        axios.get(`${apiUrl}/horse_colors`),
        axios.get(`${apiUrl}/horse_breeds`),
      ]);
      setHorses(horsesRes.data || []);
      setClients(clientsRes.data || []);
      setColors(colorsRes.data || []);
      setBreeds(breedsRes.data || []);
    } catch (err) {
      console.error('Error fetching client horses data:', err);
      setError(text.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredHorses = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return horses;
    return horses.filter((horse) => {
      const clientName = clientMap.get(Number(horse.client_id)) || '';
      return [horse.horse_name, horse.horse_origin, horse.breed, horse.rfid_chip_number, clientName]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [clientMap, horses, query]);

  const handleOpen = () => {
    setHorseData(emptyHorse);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHorseData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const formattedDate = horseData.registration_date
        ? new Date(horseData.registration_date).toLocaleDateString('en-CA')
        : null;

      const payload = {
        ...horseData,
        horse_origin: 'Outside',
        registration_date: formattedDate,
      };

      if (horseData.horse_id) {
        await axios.put(`${apiUrl}/vetclinic-horses/${horseData.horse_id}`, payload);
      } else {
        await axios.post(`${apiUrl}/vetclinic-horses`, payload);
      }

      await fetchData();
      handleClose();
    } catch (err) {
      console.error('Error saving horse:', err);
      setError(text.loadError);
    }
  };

  const handleEdit = (horse) => {
    setHorseData({
      ...emptyHorse,
      ...horse,
      registration_date: horse.registration_date ? formatDate(horse.registration_date) : '',
    });
    setOpen(true);
  };

  const handleDelete = async (horseId) => {
    if (!window.confirm(text.deleteConfirm)) return;
    try {
      await axios.delete(`${apiUrl}/vetclinic-horses/${horseId}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting horse:', err);
      setError(text.loadError);
    }
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        minHeight: '100vh',
        bgcolor: COLORS.page,
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} sx={{ borderColor: COLORS.line, color: COLORS.text }}>
              {text.refresh}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
              {text.addHorse}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
          mb: 3,
          width: '100%',
        }}
      >
        <MetricCard icon={<Horse size={26} weight="fill" />} title={text.total} value={horses.length} helper={`${filteredHorses.length} ${text.empty === 'No matching horses' ? 'shown' : 'معروضة'}`} />
        <MetricCard icon={<Horse size={26} />} title={text.clients} value={clients.length} color={COLORS.blue} soft={COLORS.blueSoft} />
        <MetricCard icon={<Horse size={26} weight="duotone" />} title={text.withRfid} value={horses.filter((h) => h.rfid_chip_number).length} color={COLORS.green} soft={COLORS.greenSoft} />
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ p: 2 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={text.search}
            size="small"
            sx={{ width: { xs: '100%', sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredHorses.length} ${text.total}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }} />
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : (
          <TableContainer sx={{ ...smartTableContainerSx, width: '100%' }}>
            <Table sx={{ ...smartTableSx, minWidth: 980 }}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{text.horse}</TableCell>
                  <TableCell>{text.origin}</TableCell>
                  <TableCell>{text.breed}</TableCell>
                  <TableCell>{text.gender}</TableCell>
                  <TableCell>{text.rfid}</TableCell>
                  <TableCell>{text.color}</TableCell>
                  <TableCell>{text.client}</TableCell>
                  <TableCell>{text.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHorses.map((horse, index) => {
                  const meta = genderMeta(horse.gender, text);
                  return (
                    <TableRow key={horse.horse_id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{horse.horse_name || '-'}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>ID {horse.horse_id}</Typography>
                      </TableCell>
                      <TableCell>{horse.horse_origin || 'Outside'}</TableCell>
                      <TableCell>{horse.breed || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" icon={meta.icon} label={meta.label} sx={{ bgcolor: meta.soft, color: COLORS.text, fontWeight: 800 }} />
                      </TableCell>
                      <TableCell>{horse.rfid_chip_number || '-'}</TableCell>
                      <TableCell>{colorMap.get(Number(horse.color_id)) || '-'}</TableCell>
                      <TableCell>{clientMap.get(Number(horse.client_id)) || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={text.editHorse}>
                          <IconButton onClick={() => handleEdit(horse)} sx={{ color: COLORS.gold }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDelete(horse.horse_id)} sx={{ color: COLORS.rose }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!filteredHorses.length && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>{text.empty}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900, color: COLORS.text }}>
          {horseData.horse_id ? text.editHorse : text.addHorse}
        </DialogTitle>
        <DialogContent>
          <TextField margin="dense" name="horse_name" label={text.horse} fullWidth value={horseData.horse_name} onChange={handleChange} required />
          <FormControl fullWidth margin="normal">
            <InputLabel>{text.breed}</InputLabel>
            <Select name="breed" label={text.breed} value={horseData.breed} onChange={handleChange} required>
              {breeds.map((breed) => (
                <MenuItem key={breed.breed_id} value={breed.breed_english}>
                  {breed.breed_english} / {breed.breed_arabic}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>{text.color}</InputLabel>
            <Select name="color_id" label={text.color} value={horseData.color_id || ''} onChange={handleChange}>
              {colors.map((color) => (
                <MenuItem key={color.color_id} value={color.color_id}>
                  {color.color_english} / {color.color_arabic}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>{text.gender}</InputLabel>
            <Select name="gender" label={text.gender} value={horseData.gender} onChange={handleChange} required>
              <MenuItem value="S">{text.stallion}</MenuItem>
              <MenuItem value="M">{text.mare}</MenuItem>
            </Select>
          </FormControl>
          <TextField margin="dense" name="rfid_chip_number" label={text.rfid} fullWidth value={horseData.rfid_chip_number} onChange={handleChange} required />
          <TextField margin="dense" name="registration_number" label={text.registrationNumber} fullWidth value={horseData.registration_number} onChange={handleChange} />
          <TextField margin="dense" name="registration_date" label={text.registrationDate} type="date" fullWidth value={horseData.registration_date || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" name="notes" label={text.notes} fullWidth multiline minRows={2} value={horseData.notes || ''} onChange={handleChange} />
          <FormControl fullWidth margin="normal">
            <InputLabel>{text.client}</InputLabel>
            <Select name="client_id" label={text.client} value={horseData.client_id || ''} onChange={handleChange}>
              {clients.map((client) => (
                <MenuItem key={client.client_id} value={client.client_id}>
                  {client.client_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ color: COLORS.text }}>{text.cancel}</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HorsesClientsList;
